export type WidgetAccessGroup =
  | "default-widgets"
  | "equities-suite-widgets"
  | "squawk-metals"
  | "squawk-multi-asset"
  | "squawk-fixed-income"
  | "squawk-energy"
  | "squawk-forex"
  | "squawk-equities";

/**
 * Filter features a widget can support.
 * Controls which sections appear in the filter/highlight library modal.
 */
export type WidgetFilterFeature =
  | "ticker-watchlists"
  | "keyword-watchlists"
  | "tag-watchlists"
  | "asset-classes"
  | "advanced-filters"
  | "sources";

export type Widget = {
  id: string;
  name: string;
  component: string;
  /** Widget is only visible to internal Newsquawk users (hidden from external users entirely) */
  internalOnly?: boolean;
  /** Widget renders its full content without authentication (e.g. TradingView charts) */
  unAuthAccessible?: boolean;
  /** Widget shows demo content (24h delayed) when not authenticated */
  demoAccessible?: boolean;
  /** Widget is hidden from the manage widget menu when not logged in */
  hideFromMenuWhenLoggedOut?: boolean;
  /** Role required to access this widget. If unset, widget is accessible to all authenticated users. */
  accessGroup?: WidgetAccessGroup;
  /** Filter features this widget supports. If unset, widget has no filter panel. */
  filterFeatures?: WidgetFilterFeature[];
};

export type WidgetGroup = {
  [key: string]: Widget[];
};

export const widgetGroups: WidgetGroup = {
  "Live News": [
    {
      id: "NewsquawkHeadlines",
      name: "Newsquawk Headlines",
      component: "NewsquawkHeadlines",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "tag-watchlists", "asset-classes", "advanced-filters"],
    },
    {
      id: "FirehoseAll",
      name: "External Firehose",
      component: "FirehoseAll",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters", "sources"],
    },
    {
      id: "CryptoWeb",
      name: "Crypto Blogs & Websites",
      component: "CryptoWeb",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "EnergyWeb",
      name: "Energy Blogs & Websites",
      component: "EnergyWeb",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "EnergyX",
      name: "Energy X (Twitter)",
      component: "EnergyX",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "TruthSocial",
      name: "Truth Social",
      component: "TruthSocial",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "StocksNews",
      name: "Equities News",
      component: "StocksNews",
      demoAccessible: true,
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "FinX",
      name: "Fin X (Twitter)",
      component: "FinX",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "GeneralWeb",
      name: "General News Web Scraper",
      component: "GeneralWeb",
      internalOnly: true,
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "FinancialReportsEU",
      name: "Financial Reports EU",
      component: "FinancialReportsEU",
      internalOnly: true,
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "ResearchHeadlines",
      name: "Newsquawk Research Reports",
      component: "ResearchHeadlines",
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "tag-watchlists", "asset-classes", "advanced-filters"],
    },
    {
      id: "StocksRatings",
      name: "Broker Ratings",
      component: "StocksRatings",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "USTraderNews",
      name: "Trade Xchange",
      component: "USTraderNews",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
  ],
  "Live Data": [
    {
      id: "EconomicCalendar",
      name: "Newsquawk Economic Calendar",
      component: "EconomicCalendar",
      accessGroup: "default-widgets",
    },
    {
      id: "InsiderTransactionFeed",
      name: "Insider Transaction Feed",
      component: "InsiderTransactionFeed",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "GovernmentTradeFeed",
      name: "Government Trade Feed",
      component: "GovernmentTradeFeed",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "OptionActivityFeed",
      name: "Option Activity Feed",
      component: "OptionActivityFeed",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "BlockTradeFeed",
      name: "Block Trade Feed",
      component: "BlockTradeFeed",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "SecFilings",
      name: "Newsquawk SEC Filings",
      component: "SecFilings",
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "SecFilingStories",
      name: "Newsquawk SEC Filing Stories",
      component: "SecFilingStories",
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "SecFilingActivity",
      name: "Newsquawk SEC Filing Activity",
      component: "SecFilingActivity",
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "SecStockFlow",
      name: "Newsquawk SEC Stock Flow",
      component: "SecStockFlow",
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "FundingDashboard",
      name: "Newsquawk Funding Dashboard",
      component: "FundingDashboard",
    },
    {
      id: "UsMacroData",
      name: "Newsquawk US Macro Data",
      component: "UsMacroData",
    },
    {
      id: "MarketSnapshot",
      name: "Newsquawk Market Snapshot",
      component: "MarketSnapshot",
    },
  ],
  "External Widgets": [
    {
      id: "TvAdvancedRealTimeChart",
      name: "Trading View Real Time Chart",
      component: "TvAdvancedRealTimeChart",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
    {
      id: "BloombergLive",
      name: "Bloomberg Business News Live",
      component: "BloombergLive",
      unAuthAccessible: true,
      hideFromMenuWhenLoggedOut: true,
      accessGroup: "default-widgets",
    },
    {
      id: "InvEconCalendar",
      name: "Investing.com Calendar",
      component: "InvEconCalendar",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
    {
      id: "TvActiveStocks",
      name: "Trading View Active Stocks",
      component: "TvActiveStocks",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
    {
      id: "TvMarketData",
      name: "Trading View Market Data",
      component: "TvMarketData",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
  ],
  Broker: [
    {
      id: "BrokerPriceLadderSP",
      name: "Price Ladder - S&P",
      component: "BrokerPriceLadderSP",
    },
    {
      id: "BrokerPriceLadderNQ",
      name: "Price Ladder - Q",
      component: "BrokerPriceLadderNQ",
    },
    {
      id: "BrokerPriceLadderYM",
      name: "Price Ladder - DOW",
      component: "BrokerPriceLadderYM",
    },
    { id: "BrokerTicket", name: "Broker Tickets", component: "BrokerTicket" },
  ],
  Other: [
    { id: "EnergyTwitter", name: "Energy Twitter", component: "EnergyTwitter" },
    { id: "FinTwitter", name: "Fin Twitter", component: "FinTwitter" },
    {
      id: "TestComponent",
      name: "Test Component",
      component: "TestComponent",
      internalOnly: true,
    },
    {
      id: "CreateHeadline",
      name: "Create Headline",
      component: "CreateHeadline",
      internalOnly: true,
    },
    {
      id: "MarketSnapshotAdmin",
      name: "Market Snapshot Admin",
      component: "MarketSnapshotAdmin",
      accessGroup: "equities-suite-widgets",
      internalOnly: true,
    },
    {
      id: "EconomicCalendar",
      name: "Economic Calendar",
      component: "EconomicCalendar",
      internalOnly: true,
    },
  ],
};

export const internalOnlyWidgetGroups = ["Broker", "Other"];

export const defaultWidgetSizes = {
  // External widgets
  TvActiveStocks: { w: 5, h: 4, x: 0, y: 0, minW: 3 },
  TvAdvancedRealTimeChart: { w: 9, h: 5, x: 0, y: 4, minW: 4 },
  InvEconCalendar: { w: 4, h: 4, x: 0, y: 0, minW: 3 },
  BloombergLive: { w: 4, h: 3, x: 0, y: 0, minW: 3 },
  TvMarketData: { w: 5, h: 4, x: 0, y: 0, minW: 3 },

  // Broker widgets
  BrokerPriceLadderSP: { w: 3, h: 7, x: 0, y: 0, minW: 2 },
  BrokerPriceLadderNQ: { w: 3, h: 7, x: 0, y: 0, minW: 2 },
  BrokerPriceLadderYM: { w: 3, h: 7, x: 0, y: 0, minW: 2 },
  BrokerTicket: { w: 4, h: 4, x: 0, y: 0, minW: 3 },

  // Twitter/social widgets
  EnergyTwitter: { w: 4, h: 4, x: 0, y: 0, minW: 3 },
  FinTwitter: { w: 4, h: 4, x: 0, y: 0, minW: 3 },

  // Internal tools
  TestComponent: { w: 4, h: 4, x: 0, y: 0, minW: 2 },
  CreateHeadline: { w: 4, h: 4, x: 0, y: 0, minW: 3 },

  // News headlines - list widgets
  NewsquawkHeadlines: { w: 5, h: 4, x: 0, y: 0, minW: 3 },
  ResearchHeadlines: { w: 4, h: 4, x: 0, y: 0, minW: 3 },

  // Table widgets
  SecFilings: { w: 8, h: 6, x: 0, y: 0, minW: 5 },
  SecFilingStories: { w: 5, h: 6, x: 0, y: 0, minW: 4 },
  SecFilingActivity: { w: 5, h: 6, x: 0, y: 0, minW: 4 },
  SecStockFlow: { w: 5, h: 5, x: 0, y: 0, minW: 3 },

  // Firehose Widgets
  FirehoseAll: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  CryptoWeb: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  EnergyWeb: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  EnergyX: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  TruthSocial: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  StocksNews: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  FinX: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  GeneralWeb: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  FinancialReportsEU: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  EconomicCalendar: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  StocksRatings: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  USTraderNews: { w: 4, h: 5, x: 0, y: 0, minW: 3 },

  // Equities widgets
  InsiderTransactionFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  GovernmentTradeFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  OptionActivityFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  BlockTradeFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  // Henry widgets
  FundingDashboard: { w: 8, h: 6, x: 0, y: 0, minW: 5 },
  UsMacroData: { w: 10, h: 6, x: 0, y: 0, minW: 6 },
  MarketSnapshot: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  MarketSnapshotAdmin: { w: 10, h: 6, x: 0, y: 0, minW: 6 },
};

export const allWidgets = Object.values(widgetGroups).flat();

const getWidgetGroup = (widgetId: string) => {
  const group = Object.keys(widgetGroups).find((group) =>
    widgetGroups[group as keyof typeof widgetGroups].some(
      (widget) => widget.id === widgetId
    )
  );
  return group;
};

export const getWidgetById = (widgetId: string) => {
  return allWidgets.find((widget) => widget.id === widgetId);
};

export const isInternalOnlyWidget = (widgetId: string) => {
  return (
    (allWidgets.find((widget) => widget.id === widgetId)?.internalOnly ??
      false) ||
    internalOnlyWidgetGroups.includes(getWidgetGroup(widgetId) ?? "")
  );
};

// Sort the widgets in each group by name
Object.keys(widgetGroups).forEach((group: string) => {
  widgetGroups[group as keyof typeof widgetGroups].sort((a, b) => {
    return a.name < b.name ? -1 : 1;
  });
});
